import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Shield, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(params.get("offer_id") || null);
  const [thread, setThread] = useState({ items: [], counterparty: "", you: "You" });
  const [draft, setDraft] = useState("");
  const [preview, setPreview] = useState({ blocked: false, info: { redactions: [], total: 0 } });
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    loadThreads();
  }, [user]);

  useEffect(() => {
    if (!activeId) return;
    loadMessages(activeId);
    const t = setInterval(() => loadMessages(activeId, true), 8000);
    return () => clearInterval(t);
  }, [activeId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread.items]);

  // Debounced preview
  useEffect(() => {
    if (!draft.trim()) { setPreview({ blocked: false, info: { redactions: [], total: 0 } }); return; }
    const id = setTimeout(async () => {
      try {
        const r = await api.get("/messages/preview", { params: { content: draft } });
        setPreview(r.data);
      } catch {}
    }, 350);
    return () => clearTimeout(id);
  }, [draft]);

  const loadThreads = async () => {
    try {
      const r = await api.get("/threads");
      setThreads(r.data.items || []);
      if (!activeId && r.data.items?.length) setActiveId(r.data.items[0].offer_id);
    } catch (e) { console.error(e); }
  };

  const loadMessages = async (offerId, silent = false) => {
    try {
      const r = await api.get(`/threads/${offerId}/messages`);
      setThread(r.data);
      if (!silent) loadThreads();
    } catch (e) { if (!silent) toast.error("Failed to load thread"); }
  };

  const send = async () => {
    if (!draft.trim() || sending) return;
    setSending(true);
    try {
      const r = await api.post(`/threads/${activeId}/messages`, { content: draft });
      if (r.data.redaction_total > 0) {
        toast.warning(`Contact info detected and redacted (${r.data.redaction_total} item${r.data.redaction_total === 1 ? "" : "s"})`);
      }
      setDraft("");
      loadMessages(activeId);
      loadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const activeThread = useMemo(() => threads.find((t) => t.offer_id === activeId), [threads, activeId]);

  if (!user) return null;

  return (
    <div data-testid="page-messages" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="font-overline text-accent">Negotiation</div>
        <h1 className="font-display text-4xl mt-2">Messages</h1>
        <p className="text-muted-foreground mt-2 text-sm flex items-center gap-2">
          <Shield className="w-4 h-4 text-primary" />
          Anonymous & private. Contact info is automatically redacted before any message is sent.
        </p>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] border border-border min-h-[60vh]">
        {/* Thread list */}
        <aside className="border-r border-border bg-card" data-testid="thread-list">
          {threads.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No threads yet. Submit an offer to start a conversation.</div>
          ) : (
            threads.map((t) => (
              <button key={t.offer_id} data-testid={`thread-item-${t.offer_id}`}
                onClick={() => setActiveId(t.offer_id)}
                className={`w-full text-left p-4 border-b border-border hover:bg-muted/40 transition-colors ${activeId === t.offer_id ? "bg-muted" : ""}`}>
                <div className="flex items-center justify-between">
                  <div className="font-overline">{t.counterparty}</div>
                  {t.unread > 0 && (
                    <span data-testid={`thread-unread-${t.offer_id}`}
                      className="bg-primary text-primary-foreground text-[10px] font-mono-data px-1.5 py-0.5 min-w-[18px] text-center">
                      {t.unread}
                    </span>
                  )}
                </div>
                <div className="text-sm font-medium mt-1 truncate">
                  {t.note ? `${t.note.vehicle_year} ${t.note.vehicle_make} ${t.note.vehicle_model}` : "—"}
                </div>
                <div className="text-xs text-muted-foreground mt-1 font-mono-data">
                  Offer {fmtMoney(t.offer_amount)} · {t.status}
                </div>
                {t.last_message?.content && (
                  <div className="text-xs text-muted-foreground mt-1 truncate">{t.last_message.content}</div>
                )}
              </button>
            ))
          )}
        </aside>

        {/* Thread view */}
        <section className="flex flex-col">
          {!activeId ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Select a thread to start chatting.</div>
          ) : (
            <>
              <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                <div>
                  <div className="font-overline text-muted-foreground">Counterparty</div>
                  <div className="font-display text-lg">{thread.counterparty}</div>
                </div>
                {activeThread?.note && (
                  <div className="text-right">
                    <div className="font-overline text-muted-foreground">Re</div>
                    <div className="text-sm">{activeThread.note.vehicle_year} {activeThread.note.vehicle_make} {activeThread.note.vehicle_model}</div>
                  </div>
                )}
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-3" data-testid="message-list">
                {thread.items.length === 0 && (
                  <div className="text-center text-muted-foreground text-sm py-12">
                    No messages yet. Be the first to send one.
                  </div>
                )}
                {thread.items.map((m) => {
                  const mine = m.sender_id === user.user_id;
                  return (
                    <div key={m.message_id}
                      className={`flex flex-col ${mine ? "items-end" : "items-start"}`}
                      data-testid={`message-${m.message_id}`}>
                      <div className={`max-w-[80%] px-4 py-2.5 border ${mine ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}>
                        <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">{m.content}</div>
                      </div>
                      <div className="text-[10px] font-mono-data text-muted-foreground mt-1 flex items-center gap-2">
                        <span>{mine ? "You" : m.sender_alias}</span>
                        <span>·</span>
                        <span>{new Date(m.created_at).toLocaleString()}</span>
                        {m.redaction_total > 0 && (
                          <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                            <AlertTriangle className="w-3 h-3" /> {m.redaction_total} redacted
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-border p-4">
                {preview.info.total > 0 && (
                  <div className="mb-2 border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-xs px-3 py-2 flex items-start gap-2"
                    data-testid="redaction-warning">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold">Contact info will be redacted before sending.</div>
                      <div className="font-mono-data opacity-90">
                        {preview.info.redactions.map((r) => `${r.count}× ${r.type}`).join(" · ")}
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Textarea
                    data-testid="message-input"
                    rows={2}
                    value={draft}
                    placeholder="Write a message… (do not share contact info — it will be redacted)"
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
                    }}
                    className="rounded-none flex-1 resize-none"
                  />
                  <Button data-testid="send-message" onClick={send} disabled={!draft.trim() || sending}
                    className="rounded-none uppercase tracking-wider text-xs h-auto">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <div className="text-[10px] text-muted-foreground mt-2 font-mono-data">
                  ⌘/Ctrl + Enter to send · Auto-redaction protects both parties
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
